/**
 * Label component for form field labels
 *
 * Styled with Tailwind CSS for dark theme.
 * Supports required field indicator.
 */

import type { LabelOptions } from '../types/form';

/**
 * Base CSS classes for the label element
 */
const BASE_CLASSES = [
  'text-sm',
  'font-medium',
  'text-foreground',
  'leading-none',
].join(' ');

/**
 * CSS classes for the required asterisk
 */
const REQUIRED_CLASSES = 'text-destructive ml-0.5';

/**
 * Create a Label element
 */
export function createLabel(options: LabelOptions): HTMLLabelElement {
  const {
    text,
    htmlFor,
    required = false,
    className = '',
    attributes = {},
  } = options;

  const label = document.createElement('label');

  // Set basic attributes
  if (htmlFor) label.htmlFor = htmlFor;

  // Set CSS classes
  label.className = `${BASE_CLASSES} ${className}`.trim();

  // Set text content
  label.textContent = text;

  // Add required asterisk if needed
  if (required) {
    const asterisk = document.createElement('span');
    asterisk.className = REQUIRED_CLASSES;
    asterisk.textContent = '*';
    asterisk.setAttribute('aria-hidden', 'true');
    label.appendChild(asterisk);
  }

  // Set additional attributes
  for (const [key, val] of Object.entries(attributes)) {
    label.setAttribute(key, val);
  }

  return label;
}

/**
 * Label class for object-oriented usage
 */
export class Label {
  private element: HTMLLabelElement;
  private options: LabelOptions;

  constructor(options: LabelOptions) {
    this.options = { ...options };
    this.element = createLabel(this.options);
  }

  /**
   * Get the label element
   */
  getElement(): HTMLLabelElement {
    return this.element;
  }

  /**
   * Set the label text
   */
  setText(text: string): this {
    this.options.text = text;
    // Keep the required asterisk if present
    const hasRequired = this.options.required;
    this.element.textContent = text;

    if (hasRequired) {
      const asterisk = document.createElement('span');
      asterisk.className = REQUIRED_CLASSES;
      asterisk.textContent = '*';
      asterisk.setAttribute('aria-hidden', 'true');
      this.element.appendChild(asterisk);
    }

    return this;
  }

  /**
   * Set the htmlFor attribute
   */
  setHtmlFor(htmlFor: string): this {
    this.element.htmlFor = htmlFor;
    return this;
  }

  /**
   * Set CSS class
   */
  setClassName(className: string): this {
    this.element.className = `${BASE_CLASSES} ${className}`.trim();
    return this;
  }
}
